import { Coords, DomEvent, DoneCallback, TileLayer, TileLayerOptions, Util } from "leaflet";
import { ITileCache } from "../../TileCache/types";

/**
 * Extension of Leaflet TileLayer adapted to use ITileCache implementations.
 */
export class LeafletCachedTileLayer extends TileLayer {

    private readonly _tileCache: ITileCache;
    private readonly _urlTemplate: string;
    private readonly _urlSubdomains: string[];

    constructor(urlTemplate: string, urlSubdomains: string[], tileCache: ITileCache, options: TileLayerOptions) {
        super(urlTemplate, options);
        this._urlTemplate = urlTemplate;
        this._urlSubdomains = urlSubdomains;
        this._tileCache = tileCache;
    }

    /**
     * Rewritten method that serves the tiles from the `IndexedDbTileCache`
     */
    public createTile(coords: Coords, done: DoneCallback): HTMLElement {
        // Rewrite of the original method...
        const tile = document.createElement("img");

        DomEvent.on(tile, "load", Util.bind((this as any)._tileOnLoad, this, done, tile));
        DomEvent.on(tile, "error", Util.bind((this as any)._tileOnError, this, done, tile));

        if (this.options.crossOrigin) {
            tile.crossOrigin = "";
        }

        /*
         Alt tag is set to empty string to keep screen readers from reading URL and for compliance reasons
         http://www.w3.org/TR/WCAG20-TECHS/H67
         */
        tile.alt = "";

        /*
         Set role="presentation" to force screen readers to ignore this
         https://www.w3.org/TR/wai-aria/roles#textalternativecomputation
         */
        tile.setAttribute("role", "presentation");

        this._tileCache.getTileAsDataUrl({
            x: coords.x,
            y: coords.y,
            z: (this as any)._getZoomForUrl(),
        }, this._urlTemplate, this._urlSubdomains).then((dataUrl: string) => {
            tile.src = dataUrl;
        }).catch(() => {
            tile.src =  this.options.errorTileUrl ?? '';
        });

        return tile;
    }

    /**
     * Clears the whole cache.
     */
    public clearCache(): Promise<boolean> {
        return this._tileCache.purgeStore();
    }
}